import { useUI } from 'app/_components/ui'
import { useAddItem } from '@framework/cart'
import { Button } from '@noissue-ui-kit/button'
import { VariantOptionSelection } from '@pageFeatures/design/designUpload/components/VariantOptionSelection'
import useProductOptionSelection from '@pageFeatures/design/designUpload/hooks/useProductOptionSelection/useProductOptionSelection'
import { generateCrossSellMetadataValue } from '@utils/crossSell'
import { useRouter, useSearchParams } from 'next/navigation'
import { useEffect, useState } from 'react'
import { TProductDetailSections } from '../useProductPageVariation'
import { DTC_QUOTE_EVENT_NAME, MISSING_SKU_ERROR } from './constants'
import { PriceSection } from './PriceSection'
import { useRequestAQuoteSubmission } from './usePlusProductDetailsSection/useRequestAQuoteSubmission.hook'
import { useProductTrackingEvents } from './useProductTrackingEvents'
import { useSession } from 'next-auth/react'
import isEmpty from 'lodash/isEmpty'
import { trackRequestAQuoteModalInteraction } from './usePlusProductDetailsSection/utils/plusForm.utils'
import { Modal } from '@noissue-ui-kit/modal'
import { productOptionToHubspotPayload } from './useCustomProductDetailSection/useCustomProductDetailSection.utils'
import * as Sentry from '@sentry/nextjs'
import { ProductVariant } from '@pageFeatures/design/designUpload/types'
import { IProductEntry } from 'app/_services/CMS/contentful/types/productEntry'
import { TotalPriceSection } from './TotalPriceSection'
import { QUANTITY_FIELD } from './useCustomProductDetailSection/useCustomProductDetailSection'

export function useStockProductDetailSection({
  productVariants,
  productData,
}: {
  productVariants: ProductVariant[]
  productData: IProductEntry
}): TProductDetailSections {
  const {
    ctaLabel,
    productSlug,
    fieldsConfiguration,
    requestAQuoteFormPath,
    alternativeDtcPagePath,
    minimumQuoteQuantityOverride,
    productName,
    enableRequestAQuoteFlow: requestAQuoteFlowEnabled,
    productPlusEditorConfig,
    extraQuoteQuantityOptions,
    correspondingPlusProductSku,
  } = productData
  const hasPlusEditorConfig = Boolean(productPlusEditorConfig)

  const router = useRouter()

  const {
    selectorsConfiguration,
    select,
    productOptionSelected,
    getQuantityOptions,
    selectedOptionLabel,
    resetProductOptionSelection,
  } = useProductOptionSelection({
    productCategorySlug: productSlug,
    productVariantsSales: productVariants,
    editorConfiguration: {
      productName: '',
      instructions: null,
      productSlug: '',
      urlSlug: '',
      guidelineConfiguration: null,
      fieldsConfiguration: fieldsConfiguration,
    },
  })

  const selectors = selectorsConfiguration()
  const productOption = productOptionSelected()
  useProductTrackingEvents(selectedOptionLabel, productOption, productName)

  const { data: session } = useSession()
  const {
    formIsLoading,
    submissionError,
    setSubmissionError,
    showSuccessDialog,
    setShowSuccessDialog,
    submitForm,
    handleUserLogin,
    preSubmittedPayload,
    saveQuoteForDefaultRAQFlow,
    savePayloadForPlusEditorFlow,
    setFormIsLoading,
  } = useRequestAQuoteSubmission({
    formEvent: DTC_QUOTE_EVENT_NAME,
    productSlug,
  })
  const [customQuantity, setCustomQuantity] = useState<number>()
  const [selectionErrors, setSelectionErrors] = useState<
    Record<string, string>
  >({})

  useEffect(() => {
    const payload = JSON.parse(preSubmittedPayload)
    if (!isEmpty(payload) && session?.email) {
      submitForm(JSON.parse(preSubmittedPayload), productSlug)
    }
  }, [preSubmittedPayload, session])

  const handleRAQSubmit = async () => {
    if (!correspondingPlusProductSku) {
      Sentry.captureException(
        `There is no correspondingPlusProductSku set for ${productSlug}. Set it up in Contentful to Unblock RAQ flow.`,
        {
          contexts: {
            'noissue.everest': {
              productOption,
            },
          },
        }
      )

      setSubmissionError(MISSING_SKU_ERROR)
      return
    }

    const payload = productOptionToHubspotPayload({
      productOption,
      customQuantity,
      productName,
      productSlug,
      correspondingPlusProductSku,
    })

    if (!session?.email) {
      const redirectUrl = hasPlusEditorConfig
        ? window?.location?.origin + `/quote/${productSlug}`
        : window?.location?.pathname

      hasPlusEditorConfig
        ? savePayloadForPlusEditorFlow(payload)
        : saveQuoteForDefaultRAQFlow(payload)

      await handleUserLogin(redirectUrl)
      return
    }

    if (hasPlusEditorConfig) {
      setFormIsLoading(true)
      savePayloadForPlusEditorFlow(payload)
      await router.push(`/quote/${productSlug}`)
    } else {
      await submitForm(payload, productSlug)
    }
  }

  const priceSection = (
    <PriceSection
      price={productOption?.price}
      salePrice={productOption?.salePrice}
      currencyCode={productOption?.currencyCode}
      // Remove "sample" part from "[number] Sample" quantity option values
      quantity={
        +productOption.quantity?.toLocaleLowerCase()?.split('sample')?.[0]
      }
    />
  )

  const addItem = useAddItem()
  const { openSidebar, openPopupSidebar } = useUI()
  const [loading, setLoading] = useState(false)
  const searchParams = useSearchParams()

  const addToCart = async () => {
    setLoading(true)
    try {
      const slug = searchParams.get('slug')
      const metadataValue = generateCrossSellMetadataValue(slug as string)

      await addItem({
        productId: String(productOption.productId),
        variantId: String(productOption.variantId),
        optionSelections: metadataValue
          ? [
              {
                option_id: productOption.metadataModifierId,
                option_value: metadataValue,
              },
            ]
          : undefined,
      })
      openPopupSidebar()
      openSidebar()
      setLoading(false)
    } catch (err) {
      setLoading(false)
    }
  }

  useEffect(() => {
    setSubmissionError(null)
  }, [customQuantity])

  const [selectedQuantity, setSelectedQuantity] = useState<number>()
  const [raqSelected, setRaqSelected] = useState<boolean>(false)

  const onSelection = ({ name, value }) => {
    if (name === QUANTITY_FIELD) {
      setSelectedQuantity(value)
    }
    select({ name, value })
  }

  useEffect(() => {
    const selectedRAQOption =
      selectedQuantity && String(selectedQuantity) !== productOption['quantity']
    const enteredCustomOption = Boolean(customQuantity)

    setRaqSelected(enteredCustomOption || selectedRAQOption)
  }, [selectedQuantity, customQuantity])

  const mainSection = (
    <>
      {selectors
        .map((option) => {
          if (option.values.length === 1 && !option.forceShow) {
            return null
          }

          let selectedValue = productOption?.[option.name]

          if (option.name === QUANTITY_FIELD) {
            selectedValue =
              customQuantity || selectedQuantity || productOption?.[option.name]
          }

          return (
            <VariantOptionSelection
              selectChange={onSelection}
              selectedValue={
                selectedValue
                  ? {
                      key: String(selectedValue),
                      displayLabel: String(selectedValue),
                    }
                  : undefined
              }
              key={option.name}
              option={option}
              getQuantityOptions={getQuantityOptions}
              onError={({ field, error }) => {
                setSelectionErrors({
                  ...selectionErrors,
                  [field]: error,
                })
              }}
              customQuantity={customQuantity}
              setCustomQuantity={setCustomQuantity}
              requestAQuoteFormPath={requestAQuoteFormPath}
              alternativeDtcPagePath={alternativeDtcPagePath}
              minimumQuoteQuantityOverride={minimumQuoteQuantityOverride}
              includeQuoteOption={true}
              requestAQuoteFlowEnabled={requestAQuoteFlowEnabled}
              extraQuoteQuantityOptions={extraQuoteQuantityOptions?.split(',')}
            />
          )
        })
        .filter(Boolean)}

      {!raqSelected && <TotalPriceSection price={productOption?.price} />}

      <hr className="mb-10 h-[1px] bg-pebble" />

      {raqSelected && requestAQuoteFlowEnabled && submissionError && (
        <p className="mb-8 text-xl text-error font-mori">{submissionError}</p>
      )}

      {raqSelected && requestAQuoteFlowEnabled ? (
        <Button
          size={'m'}
          corners={'rounded'}
          className="w-full"
          onClick={() => {
            handleRAQSubmit()
          }}
          loading={formIsLoading}
          disabled={
            Boolean(Object.values(selectionErrors)?.filter(Boolean)?.length) ||
            Boolean(submissionError)
          }
        >
          {hasPlusEditorConfig ? 'Continue' : 'Request a quote'}
        </Button>
      ) : (
        <Button
          size={'m'}
          corners={'rounded'}
          className="w-full"
          onClick={addToCart}
          loading={loading}
          ariaLabel={'Add To Cart'}
        >
          {ctaLabel || 'Add To Cart'}
        </Button>
      )}

      <Modal
        opened={showSuccessDialog}
        size="medium"
        title={'Quote Requested!'}
        confirmLabel={'Shop all'}
        cancelLabel="Request a quote (same product)"
        onConfirm={() => {
          trackRequestAQuoteModalInteraction({
            componentValue: 'Shop all',
            productSlug,
          })
          router.push('/category/all/')
        }}
        onCancel={() => {
          setCustomQuantity(null)
          resetProductOptionSelection()
          trackRequestAQuoteModalInteraction({
            componentValue: 'Request a quote (same product)',
            productSlug,
          })
          setShowSuccessDialog(false)
        }}
        showControls={{
          cancelIcon: true,
          cancelButton: true,
          confirmButton: true,
        }}
        confirmButtonDisabled={false}
      >
        <p className="text-2xl text-acai">
          Nice one! An Account Manager will be in touch with your quote within
          24-48 hours, via email.
        </p>
      </Modal>
    </>
  )

  return {
    priceSection,
    mainSection,
  }
}
